import React, { useEffect, useState } from 'react';
import type { AdminPlanTemplate } from '@brand24-original/llm-types';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import SimpleBar from 'simplebar-react';
import './PlanTemplatesDialog.scss';
import {
  createPlanTemplate,
  deletePlanTemplate,
  getPlanTemplates,
  updatePlanTemplate,
} from '../../../../../shared/services/admin/users/AdminUsersService';
import { allMonitorTypes } from '../../../../../shared/types/monitor/MonitorType';
import { addonDescriptions, allAddons } from '../../../../../shared/types/account/Addons';
import useToast from '../../../../../shared/hooks/global/UseToast';

interface PlanTemplatesDialogProps {
  visible: boolean;
  onHide: () => void;
  /** Read-only admins can browse the templates but not change them. */
  canEdit: boolean;
  /** Called after every create / update / delete with the fresh list, so the opener can refresh its picker. */
  onChanged?: (templates: AdminPlanTemplate[]) => void;
  /** Lists to prefill a NEW template with (the account page passes what is ticked there). */
  initialFeatures?: string[];
  initialAddons?: string[];
}

const EMPTY_ID = 0;

/**
 * Manage plan templates (named model + addon sets) anywhere in the admin: list on the left, the picked one
 * (or a new one) edited on the right. Applying a template to an account happens on the account page.
 */
const PlanTemplatesDialog: React.FC<PlanTemplatesDialogProps> = ({
  visible,
  onHide,
  canEdit,
  onChanged,
  initialFeatures = [],
  initialAddons = [],
}) => {
  const { showToast, toastElement } = useToast();
  const [templates, setTemplates] = useState<AdminPlanTemplate[] | null>(null);
  const [editingId, setEditingId] = useState<number>(EMPTY_ID);
  const [name, setName] = useState<string>('');
  const [features, setFeatures] = useState<string[]>([]);
  const [addons, setAddons] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);

  const startNew = () => {
    setEditingId(EMPTY_ID);
    setName('');
    setFeatures(initialFeatures);
    setAddons(initialAddons);
  };

  const startEdit = (template: AdminPlanTemplate) => {
    setEditingId(template.id);
    setName(template.name);
    setFeatures(template.features);
    setAddons(template.addons);
  };

  useEffect(() => {
    if (!visible) return;
    startNew();
    getPlanTemplates().then(setTemplates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const publish = (next: AdminPlanTemplate[]) => {
    const sorted = [...next].sort((a, b) => a.name.localeCompare(b.name));
    setTemplates(sorted);
    onChanged?.(sorted);
  };

  const toggle = (list: string[], setList: (next: string[]) => void, value: string, checked: boolean) =>
    setList(checked ? [...list, value] : list.filter((item) => item !== value));

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || !templates) return;
    setSaving(true);
    try {
      if (editingId === EMPTY_ID) {
        const created = await createPlanTemplate({ name: trimmed, features, addons });
        publish([...templates, created]);
        startEdit(created);
        showToast(`Template "${created.name}" created`);
      } else {
        const updated = await updatePlanTemplate(editingId, { name: trimmed, features, addons });
        publish(templates.map((template) => (template.id === updated.id ? updated : template)));
        showToast(`Template "${updated.name}" saved`);
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    const template = templates?.find((item) => item.id === editingId);
    if (!template) return;
    confirmDialog({
      message: `Delete the template "${template.name}"? Accounts it was applied to keep their settings.`,
      header: 'Delete plan template',
      defaultFocus: 'reject',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      className: 'dialog-box',
      accept: async () => {
        try {
          await deletePlanTemplate(template.id);
          publish((templates ?? []).filter((item) => item.id !== template.id));
          startNew();
          showToast('Template deleted');
        } catch (err: any) {
          showToast(err?.response?.data?.error || 'Failed to delete template', 'error');
        }
      },
    });
  };

  const renderOptions = (
    title: string,
    options: string[],
    selected: string[],
    setSelected: (next: string[]) => void,
    describe?: (option: string) => string
  ) => (
    <div className="plan-templates__group">
      <div className="plan-templates__group-title">
        {title} <span className="plan-templates__count">{selected.length} on</span>
      </div>
      <SimpleBar className="plan-templates__options" autoHide={false}>
        {options.map((option) => (
          <div key={option} className="plan-templates__option" title={describe?.(option)}>
            <Checkbox
              checked={selected.includes(option)}
              disabled={!canEdit}
              onChange={(e) => toggle(selected, setSelected, option, Boolean(e.checked))}
            />
            <span>{option}</span>
          </div>
        ))}
      </SimpleBar>
    </div>
  );

  return (
    <Dialog
      header="Plan templates"
      visible={visible}
      onHide={onHide}
      className="dialog-box plan-templates"
      footer={
        <div className="plan-templates__footer">
          {canEdit && editingId !== EMPTY_ID && (
            <Button
              type="button"
              className="btn btn--sm btn--secondary plan-templates__delete"
              onClick={remove}
            >
              <span className="ico--trash ico--xs" />
              Delete
            </Button>
          )}
          <Button type="button" className="btn btn--sm btn--secondary" label="Close" onClick={onHide} />
          {canEdit && (
            <Button
              type="button"
              className="btn btn--sm"
              disabled={saving || name.trim().length === 0}
              onClick={() => void save()}
            >
              <span className="ico--tick ico--xs" />
              {editingId === EMPTY_ID ? 'Create template' : 'Save template'}
            </Button>
          )}
        </div>
      }
    >
      {toastElement}
      <div className="plan-templates__body">
        <div className="plan-templates__list">
          {canEdit && (
            <button
              type="button"
              className={`plan-templates__item${
                editingId === EMPTY_ID ? ' plan-templates__item--active' : ''
              }`}
              onClick={startNew}
            >
              <span className="ico--add ico--xs" />
              New template
            </button>
          )}
          {templates === null && <div className="plan-templates__empty">Loading…</div>}
          {templates?.length === 0 && <div className="plan-templates__empty">No templates yet.</div>}
          {templates?.map((template) => (
            <button
              key={template.id}
              type="button"
              className={`plan-templates__item${
                editingId === template.id ? ' plan-templates__item--active' : ''
              }`}
              onClick={() => startEdit(template)}
            >
              <span className="plan-templates__item-name">{template.name}</span>
              <span className="plan-templates__item-meta">
                {template.features.length} models · {template.addons.length} addons
              </span>
            </button>
          ))}
        </div>
        <div className="plan-templates__editor">
          <label className="plan-templates__name">
            <span>Name</span>
            <InputText
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Agency full, Trial PL"
              maxLength={80}
              disabled={!canEdit}
            />
          </label>
          <div className="plan-templates__groups">
            {renderOptions('AI models', allMonitorTypes, features, setFeatures)}
            {renderOptions(
              'Addons',
              allAddons,
              addons,
              setAddons,
              (addon) => addonDescriptions[addon as keyof typeof addonDescriptions]
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default PlanTemplatesDialog;
